import { ServerSnapInfo } from '@/api/type-server'
import {
  IMAGE_STATUS_ERROR_MAP,
  MISMATCH_REASON_MAP,
  SNAP_STATUS_MAP,
  SnapshotImageStatus,
  SnapStatus,
} from '@/consts/snapshot'

// Re-export enums for backward compatibility
export { SnapshotImageStatus, SnapStatus }

/**
 * Snapshot model representing a saved instance state
 * Transforms server response data into a typed client-side model
 */
export class Snapshot {
  id = 0
  resID = ''
  flagID = ''
  name = ''
  path = ''
  enabled = false
  createdAt = new Date()
  status = SnapStatus.Invalid
  imageStatus = SnapshotImageStatus.Invalid
  mismatchReason = 0
  size = 0
  minDiskGB = 0
  presetPorts: Array<{ srcPort: number; protocol: number }> = []

  /**
   * Constructs a Snapshot instance from server response data
   * Performs simple type conversions: string -> number, string -> Date
   */
  constructor(props: ServerSnapInfo) {
    this.id = Number(props.id)
    this.resID = props.resID
    this.flagID = props.flag?.id ?? ''
    this.name = props.flag?.subject ?? ''
    this.path = props.path
    this.enabled = props.enabled
    this.createdAt = new Date(props.generatedTime)
    this.status = props.status
    this.imageStatus = props.imageStatus
    this.mismatchReason = props.mismatchReason
    this.size = Number(props.sizeByte)
    this.minDiskGB = props.minDiskGB ?? 0
    this.presetPorts = (props.nodeInfo?.node?.image?.ports ?? []).map((port) => ({
      srcPort: Number(port.srcPort),
      protocol: port.protocol,
    }))
  }

  /**
   * Returns true if snapshot cannot be used due to image or compatibility issues
   */
  get disabled() {
    return this.imageStatus !== SnapshotImageStatus.OK || !!this.mismatchReason
  }

  /**
   * Returns a human-readable error message for disabled snapshots
   */
  get errMsg() {
    // Check for image status errors first
    const imageError = IMAGE_STATUS_ERROR_MAP[this.imageStatus]
    if (imageError) {
      return imageError
    }

    // Check for mismatch reason errors
    const mismatchError = MISMATCH_REASON_MAP[this.mismatchReason]
    if (mismatchError) {
      return mismatchError
    }

    // Check if image is disabled/expired
    if (!this.enabled) {
      return 'The underlying image has expired'
    }

    return 'Unknown error'
  }

  /**
   * Returns the display label and style class for the current status
   */
  get statusText() {
    return SNAP_STATUS_MAP[this.status]
  }
}
